/**
 * FIRECON DAYS — steam stood down for fire risk (Chris, 2026-09-11, item c).
 *
 * "This is an enable or disable option on the Brief modelling panel. If enabled
 * then it will ask for a number of consecutive non-steam days in May, June, July
 * and August (e.g. 3, 2, 4, 3). The timetable builds only diesel services on
 * these days, marks them as Firecon days on the timetable and the exec PDF;
 * leave day one as normal sales and day two and three as 30% down on the
 * previous year." And later: "let me tweak the % reduction for each of the days."
 *
 * What this module owns (pure, gated — .studiotest/firecon.test.ts):
 *   - WHICH dates are Firecon days: `fireconDays` — for each configured month, N
 *     consecutive RUNNING days, starting at the planner's start date (the first
 *     running day on or after it) or, with none set, the month's first running
 *     day. Consecutive running days, not calendar days: a stand-down that spans
 *     a non-running day still costs the railway N running days.
 *   - HOW MUCH the sales fall: `reductions[k]` is the % down on the like day for
 *     day k+1 of the run; a run longer than the list takes the last figure.
 *     Default day one 0%, then 30%.
 *   - WHICH steam may still run: an OIL-FIRED engine in the register (2253
 *     Omaha). Coal steam is stood down; without a register no steam runs.
 *
 * The rest is applied by the owners that already exist: the per-date valuation
 * (`perDateValue.ts::valueDepsOnDay` takes the demand scale, `daySide` carries
 * the Firecon tag), the traction designation (`diagrams.ts::designateDiagrams`
 * with `{ firecon: true }`), the cost (`costs.ts::costFromDeps`), the WSS cells,
 * the board paper and the workbook. Modelled side only — today's side is what
 * the railway actually ran.
 */
import type { FleetLoco } from './types'

export interface FireconConfig {
  enabled: boolean
  /** Consecutive non-steam RUNNING days per calendar month (keys '5'…'8' = May…August). */
  days: Record<string, number>
  /** Optional start date per month ('YYYY-MM-DD'): the run starts at the first
   *  running day on or after it. Blank = the month's first running day. */
  starts?: Record<string, string>
  /** Sales against the previous year, % DOWN, by day of the run (index 0 = day
   *  one). Days beyond the list take the last figure. */
  reductions: number[]
}

/** The months a stand-down may fall in — May to August. */
export const FIRECON_MONTHS: readonly number[] = [5, 6, 7, 8]

export const DEFAULT_FIRECON: FireconConfig = {
  enabled: false,
  days: { '5': 3, '6': 2, '7': 4, '8': 3 },
  reductions: [0, 30, 30],
}

/** One Firecon date: which day of the run it is (1-based) and its sales reduction (%). */
export interface FireconDay { day: number; pct: number }

const clampPct = (v: unknown): number => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.min(100, Math.max(0, n))
}

/** The % reduction for day `day` (1-based) of a run. */
export function fireconReduction(cfg: FireconConfig, day: number): number {
  const r = cfg.reductions ?? []
  if (r.length === 0) return 0
  return clampPct(r[Math.min(day, r.length) - 1])
}

/**
 * The Firecon dates of one month among its running dates (any format
 * 'YYYY-MM-DD'; dates of other months are ignored), each with its day of the
 * run and its reduction. Empty when the option is off or the month has none.
 */
export function fireconDays(cfg: FireconConfig | null | undefined, month: number, runningDates: string[]): Map<string, FireconDay> {
  const out = new Map<string, FireconDay>()
  if (!cfg?.enabled) return out
  const n = Math.max(0, Math.floor(Number(cfg.days?.[String(month)] ?? 0)))
  if (n === 0) return out
  const dates = [...new Set(runningDates)].filter((d) => Number(d.slice(5, 7)) === month).sort()
  if (dates.length === 0) return out
  const start = cfg.starts?.[String(month)]
  let i0 = start ? dates.findIndex((d) => d >= start) : 0
  if (i0 < 0) i0 = 0 // a start after the month's last running day — the run falls at the front, and the panel says so
  for (let k = 0; k < n && i0 + k < dates.length; k++) out.set(dates[i0 + k], { day: k + 1, pct: fireconReduction(cfg, k + 1) })
  return out
}

/** The demand multiplier for a date: 1 on a normal day, 1 − pct on a Firecon day. */
export const fireconScale = (d: FireconDay | null | undefined): number => (d ? Math.max(0, 1 - d.pct / 100) : 1)

/** The words every surface prints for a Firecon date. */
export const fireconLabel = (d: FireconDay): string => `Firecon day ${d.day} · steam stood down${d.pct > 0 ? ` · sales −${d.pct}%` : ''}`

/** The steam engines that may run on a Firecon day: the ticked OIL-FIRED ones. */
export const fireconSteam = (locos: FleetLoco[] | undefined): FleetLoco[] =>
  (locos ?? []).filter((l) => l.inSimulation && l.traction === 'steam' && l.fuel === 'oil')

/** How many Firecon days the configuration asks for across the season. */
export const fireconTotalDays = (cfg: FireconConfig | null | undefined): number =>
  cfg?.enabled ? FIRECON_MONTHS.reduce((t, m) => t + Math.max(0, Math.floor(Number(cfg.days?.[String(m)] ?? 0))), 0) : 0

/** A saved configuration (a session's state, the railway default) made safe to use. */
export function normaliseFirecon(raw: unknown): FireconConfig {
  const r = (raw ?? {}) as Partial<FireconConfig>
  const days: Record<string, number> = {}
  for (const m of FIRECON_MONTHS) {
    const v = Number((r.days ?? {})[String(m)] ?? DEFAULT_FIRECON.days[String(m)])
    days[String(m)] = Number.isFinite(v) ? Math.min(31, Math.max(0, Math.floor(v))) : 0
  }
  const starts: Record<string, string> = {}
  for (const [k, v] of Object.entries(r.starts ?? {})) if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) starts[k] = v
  const reductions = Array.isArray(r.reductions) && r.reductions.length > 0 ? r.reductions.map(clampPct) : DEFAULT_FIRECON.reductions.slice()
  return { enabled: !!r.enabled, days, ...(Object.keys(starts).length > 0 ? { starts } : {}), reductions }
}
